import React, { useEffect, useState, useMemo } from 'react';

import { Link } from '@arctheme-components/elements/Link/Link';
import Headings from '@src/stories/elements/Headings/Headings';
import Modal from '@src/stories/elements/Modal/Modal';
import Paragraph from '@src/stories/elements/Paragraph/Paragraph';
import ButtonWithChildren from '@arctheme-components/elements/Button/ButtonWithChildren';

import helpers from 'frontend/js/helpers';

// Types
interface CountryRedirect {
  country_code: string;
  redirect_url: string;
  button_label: string;
}

interface GeolocationPopupProps {
  countryRedirects: CountryRedirect[];
  settings: any;
  colorSchema: string;
}

const GEO_API = 'https://ipapi.co/json/';
const COOKIE_NAME = 'geolocation-closed';

export const GeolocationPopup = ({ countryRedirects, settings, colorSchema }: GeolocationPopupProps) => {
  const {
    max_width,
    header_margin_bottom,
    header_margin_bottom_mobile,
    header_alignment,
    heading,
    heading_type,
    heading_margin_bottom,
    heading_margin_bottom_mobile,
    heading_accent_color,
    heading_accent_parts,
    description,
    body_type,

    padding_top_desktop,
    padding_bottom_desktop,
    padding_left_right_desktop,
    padding_top_mobile,
    padding_bottom_mobile,
    padding_left_right_mobile,

    close_button_position,

    cookie_expiry,
    show_delay,
    show_once
  } = settings;

  const [customerCountryCode, setCustomerCountryCode] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const shopCountryCode = (window as any).Shopify.country;

  // Sort countryRedirects so that the redirect matching the customer's country code comes first
  const sortedCountryRedirects = useMemo(() => {
    if (!customerCountryCode) return [...countryRedirects];

    const match = countryRedirects.find((redirect) => redirect.country_code === customerCountryCode);

    const others = countryRedirects
      .filter((redirect) => redirect.country_code !== customerCountryCode)
      .sort((a, b) => a.country_code.localeCompare(b.country_code));
    return match ? [match, ...others] : others;
  }, [countryRedirects, customerCountryCode]);

  const handleClose = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    // Check if the popup has been closed before
    if (show_once && helpers.getCookie(COOKIE_NAME)) return;

    const showWithDelay = () => {
      if (show_delay && !isNaN(Number(show_delay))) {
        setTimeout(() => setShowPopup(true), Number(show_delay) * 1000);
      } else {
        setShowPopup(true);
      }

      helpers.setCookie(COOKIE_NAME, 'true', cookie_expiry);
    };

    // Check if the popup should be shown based on the cached country code if available
    const cachedCustomerCountryCode = localStorage.getItem('customerCountryCode');

    if (cachedCustomerCountryCode) {
      if (cachedCustomerCountryCode !== shopCountryCode) {
        setCustomerCountryCode(cachedCustomerCountryCode);
        showWithDelay();
      }

      return;
    }

    // Fetch the customer's country code from the API
    fetch(GEO_API)
      .then((res) => res.json())
      .then((data) => {
        const { country_code: customerCountryCode } = data;

        // Store the country code in local storage
        // This is to avoid fetching the API again if the user refreshes the page and hitting the API rate limits
        localStorage.setItem('customerCountryCode', customerCountryCode);
        setCustomerCountryCode(customerCountryCode);

        if (customerCountryCode !== shopCountryCode) {
          showWithDelay();
        }
      })
      .catch(() => {
        setCustomerCountryCode(null);
        setShowPopup(false);
        console.log('Error fetching geolocation data');
      });
  }, [countryRedirects, shopCountryCode]);

  return (
    <Modal
      isOpen={showPopup}
      onClickMask={handleClose}
      lockBody={true}
    >
      <div
        className={`color-${colorSchema} rounded-3xl pt-[${padding_top_mobile}px] lg:pt-[${padding_top_desktop}px] pb-[${padding_bottom_mobile}px] lg:pb-[${padding_bottom_desktop}px] px-[${padding_left_right_mobile}px] lg:px-[${padding_left_right_desktop}px] max-w-[${max_width}px] w-[90vw] shadow-lg`}
      >
        <div className={`mb-[${header_margin_bottom_mobile}px] lg:mb-[${header_margin_bottom}px] flex flex-col ${header_alignment}`}>
          <Headings
            text={helpers.renderAccentText(heading, heading_accent_parts, heading_accent_color)}
            type={'h3'}
            classes={`${heading_type} mb-[${heading_margin_bottom_mobile}px] lg:mb-[${heading_margin_bottom}px]`}
          />

          <Paragraph
            text={description}
            type={body_type}
            maxChars={10000}
          />
        </div>

        <div className='flex flex-col gap-4'>
          {sortedCountryRedirects.map((redirect) => {
            const { country_code, redirect_url, button_label } = redirect;

            return (
              <Link
                key={country_code}
                link={redirect_url}
                classes={`button !m-0 ${country_code === customerCountryCode ? 'button-primary' : 'button-secondary'}`}
              >
                {button_label}
              </Link>
            );
          })}
        </div>

        <ButtonWithChildren
          type='button'
          onClick={handleClose}
          classes={`popup-close-btn absolute top-4 ${
            close_button_position === 'left' ? 'left-4' : 'right-4'
          } bg-black text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-800 focus:outline-none z-10`}
        >
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M6 18L18 6M6 6l12 12'
            ></path>
          </svg>
        </ButtonWithChildren>
      </div>
    </Modal>
  );
};
